import {
	Alert,
	Button,
	Content,
	ContentVariants,
	Form,
	FormGroup,
	Grid,
	GridItem,
	Progress,
	ProgressMeasureLocation,
	Split,
	SplitItem,
	Stack,
	StackItem,
	Title,
} from "@patternfly/react-core";
import { AngleLeftIcon, AngleRightIcon, OutlinedCommentsIcon } from "@patternfly/react-icons";
import { FormTextInput } from "@rhc-shared-components/form-text-input";
import { PhoneNumberComponent } from "@rhc-shared-components/phone-number-input";
import { RichTextEditorFormComponent } from "@rhc-shared-components/rich-text-editor";
import type { FormikProps } from "formik";
import type { FC } from "react";
import { useState } from "react";
import {
	PRODUCT_GENERAL_LONG_DESCRIPTION_MAXLENGTH,
	PRODUCT_QUICK_START_MAXLENGTH,
	PRODUCT_SUPPORT_MAXLENGTH,
} from "../../../constants/validation";
import { ArrayEditable, ArrayReadOnly } from "./ArrayFieldRenderers";
import FeedbackModal from "./FeedbackModal";
import type { EditMode, FieldValue, ReviewField } from "./types";
import "./AIFieldReview.scss";

const RICHTEXT_FIELD_CONFIG: Record<
	string,
	{ maxLength: number; showMaxLengthText: boolean; showCodeBlock?: boolean }
> = {
	"descriptions.long": {
		maxLength: PRODUCT_GENERAL_LONG_DESCRIPTION_MAXLENGTH,
		showMaxLengthText: true,
	},
	quick_start_configuration: {
		maxLength: PRODUCT_QUICK_START_MAXLENGTH,
		showMaxLengthText: true,
		showCodeBlock: true,
	},
	"support.description": {
		maxLength: PRODUCT_SUPPORT_MAXLENGTH,
		showMaxLengthText: true,
	},
};

export interface AIFieldReviewBodyProps {
	currentField: ReviewField;
	totalFields: number;
	pendingCount: number;
	statusMessage: { text: string; variant: "success" | "info" };
	formikProps: FormikProps<{ fieldValue: FieldValue }>;
	onPrevious: () => void;
	onNext: () => void;
	hasPrevious: boolean;
	hasNext: boolean;
	editMode: EditMode;
	generationId: string;
}

const ReadOnlyValue: FC<{ value: FieldValue | null; inputType: string; fieldKey: string }> = ({
	value,
	inputType,
	fieldKey,
}) => {
	if (inputType === "array") {
		return <ArrayReadOnly value={value} fieldKey={fieldKey} />;
	}
	if (inputType === "richtext" && typeof value === "string") {
		// biome-ignore lint/security/noDangerouslySetInnerHtml: trusted CKEditor HTML from API
		return <Content dangerouslySetInnerHTML={{ __html: value }} />;
	}
	if (
		(fieldKey === "support.url" ||
			fieldKey === "legal.license_agreement_url" ||
			fieldKey === "legal.privacy_policy_url") &&
		typeof value === "string"
	) {
		return (
			<Content>
				<Content component={ContentVariants.a} href={value || "#"} target="_blank" rel="noopener noreferrer">
					{value}
				</Content>
			</Content>
		);
	}
	return (
		<Content>
			<Content component={ContentVariants.p}>{(value as string) ?? ""}</Content>
		</Content>
	);
};

const AISuggestionLabel: FC<{ onFeedbackClick: () => void }> = ({ onFeedbackClick }) => (
	<Split>
		<SplitItem isFilled>
			<FormGroup
				label={
					<>
						<span className="pf-v5-svg" style={{ marginRight: "var(--pf-t--global--spacer--xs)" }}>
							&#10022;
						</span>
						AI generated suggestion
					</>
				}
				fieldId="ai-value"
			/>
		</SplitItem>
		<SplitItem>
			<Button variant="link" isInline icon={<OutlinedCommentsIcon />} onClick={onFeedbackClick}>
				Send feedback
			</Button>
		</SplitItem>
	</Split>
);

const AIFieldReviewBody: FC<AIFieldReviewBodyProps> = ({
	currentField,
	totalFields,
	pendingCount,
	statusMessage,
	formikProps,
	onPrevious,
	onNext,
	hasPrevious,
	hasNext,
	editMode,
	generationId,
}) => {
	const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

	return (
		<>
			<Form onSubmit={formikProps.handleSubmit}>
				<Stack hasGutter>
					<StackItem>
						<Alert variant={statusMessage.variant} isInline title={statusMessage.text} />
					</StackItem>

					<StackItem>
						<Alert variant="info" isInline title="Always review AI-generated content prior to use." />
					</StackItem>

					<StackItem>
						<Content>
							<Content component={ContentVariants.small}>
								Review and save fields to enhance your product information. Fields not saved will be left unchanged.
							</Content>
						</Content>
					</StackItem>

					<StackItem>
						<div className="ai-field-review__progress">
							<div className="ai-field-review__progress-header">
								<Content component={ContentVariants.p}>Product field review</Content>
								<Content component={ContentVariants.p}>{pendingCount} remaining</Content>
							</div>
							<div className="ai-field-review__progress-controls">
								<div className="ai-field-review__progress-bar">
									<Progress
										value={((totalFields - pendingCount) / totalFields) * 100}
										measureLocation={ProgressMeasureLocation.none}
										aria-label="Field review progress"
									/>
								</div>
								<Button
									icon={<AngleLeftIcon />}
									variant="plain"
									isDisabled={!hasPrevious}
									onClick={onPrevious}
									aria-label="Previous field"
								/>
								<Button
									icon={<AngleRightIcon />}
									variant="plain"
									isDisabled={!hasNext}
									onClick={onNext}
									aria-label="Next field"
								/>
							</div>
						</div>
					</StackItem>

					<StackItem>
						<Title headingLevel="h3" size="md">
							{currentField.label}
						</Title>
					</StackItem>

					{/* Conflict: side-by-side comparison */}
					{currentField.hasConflict && (
						<StackItem>
							<Grid hasGutter>
								<GridItem span={6}>
									<Stack className="ai-field-review__comparison-col">
										<StackItem>
											<FormGroup label="Existing version" fieldId="existing-value" />
										</StackItem>
										<StackItem isFilled>
											<ReadOnlyValue
												value={currentField.existingValue}
												inputType={currentField.inputType}
												fieldKey={currentField.key}
											/>
										</StackItem>
									</Stack>
								</GridItem>
								<GridItem span={6}>
									<Stack className="ai-field-review__comparison-col">
										<StackItem>
											<AISuggestionLabel onFeedbackClick={() => setIsFeedbackOpen(true)} />
										</StackItem>
										<StackItem isFilled>
											<ReadOnlyValue
												value={currentField.aiValue}
												inputType={currentField.inputType}
												fieldKey={currentField.key}
											/>
										</StackItem>
									</Stack>
								</GridItem>
							</Grid>
						</StackItem>
					)}

					{/* No conflict: AI suggestion only */}
					{!currentField.hasConflict && (
						<StackItem>
							<Stack hasGutter>
								<StackItem>
									<AISuggestionLabel onFeedbackClick={() => setIsFeedbackOpen(true)} />
								</StackItem>
								<StackItem>
									<ReadOnlyValue
										value={currentField.aiValue}
										inputType={currentField.inputType}
										fieldKey={currentField.key}
									/>
								</StackItem>
							</Stack>
						</StackItem>
					)}

					{/* Editable field (after user accepts/edits) */}
					{editMode !== "none" && (
						<StackItem>
							<FormGroup label={`${currentField.label} to be saved`} fieldId="ai-field-value">
								{currentField.inputType === "richtext" ? (
									<RichTextEditorFormComponent
										name="fieldValue"
										label=""
										{...RICHTEXT_FIELD_CONFIG[currentField.key]}
									/>
								) : currentField.inputType === "array" ? (
									<ArrayEditable fieldKey={currentField.key} values={formikProps.values.fieldValue} />
								) : currentField.inputType === "phone" ? (
									<PhoneNumberComponent
										name="fieldValue"
										label=""
										defaultCountry="US"
										international
										placeholder="Please include country code"
									/>
								) : (
									<FormTextInput name="fieldValue" label="" type="text" />
								)}
							</FormGroup>
						</StackItem>
					)}
				</Stack>
			</Form>
			<FeedbackModal isOpen={isFeedbackOpen} onClose={() => setIsFeedbackOpen(false)} generationId={generationId} />
		</>
	);
};

export default AIFieldReviewBody;
